<template>
    <div id="analytics">
      <div class="row">
        <div class="col-md-12">
          <h6 class="page-title">Report Analytics</h6>
          <div class="retention-guide">
            <p class="mb-5">The report below indicates the number of times a specific Dealer has taken the Assessment and completed an Action Plan along with the Retention Priority that has been chosen as a topic of focus for the Dealership.</p>
          </div>

          <b-table
            id="analytics-table"
            ref="analyticsTable"
            fixed
            striped
            hover
            :filter="filter"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            :items="analytics"
            :fields="fields"
            :per-page="perPage"
            :current-page="currentPage"
            :busy="!analytics">
            <template #thead-top>
              <b-tr>
                <b-th colspan="2">

                  <b-input-group>
                    <b-input-group-prepend>
                      <span class="input-group-text"><font-awesome-icon class="fa-fw" :icon="['fas', 'search']"/></span>
                    </b-input-group-prepend>
                    <b-form-input v-model="filter" placeholder="Search By Dealer Name and/or P&A Code" debounce="500">
                    </b-form-input>
                  </b-input-group>

                </b-th>
                <b-th></b-th>
                <b-th></b-th>
                <b-th>
                  <b-button v-if="analytics" class="float-right action-plan-btn" pill @click="exportToCSV" :disabled="noResults">
                    <span v-if="!filter">Export All</span>
                    <span v-else>Export Search</span>
                    <font-awesome-icon class="fa-fw" :icon="['far', 'chevron-right']"/>
                  </b-button>
                </b-th>
              </b-tr>
            </template>
          </b-table>

          <b-pagination
            v-model="currentPage"
            :total-rows="rows"
            :per-page="perPage"
            aria-controls="analytics-table"
          ></b-pagination>
        </div>
      </div>
    </div>
</template>

<script>
import ApiService from "../services/api.service";
import { jsonToCSV } from '@/lib/csv_helper';
import { localDownload } from '@/lib/download_helper';
import { format } from 'date-fns';
import { debounce } from 'lodash';

export default {
  name: "report-analytics",
  data() {
    return {
      currentPage: 1,
      perPage: 25,
      filter: null,
      sortBy: 'dealership',
      sortDesc: false,
      fields: [
        {
          key: 'pa_code',
          sortable: true,
          label: 'P&A Code',
          thClass: 'ford-border ford-border-th',
          tdClass: 'ford-border ford-border-td'
        },
        {
          key: 'dealership_name',
          sortable: true,
          label: 'Dealership Name',
          thClass: 'ford-border ford-border-th',
          tdClass: 'ford-border ford-border-td'
        },
        {
          key: 'completed_assessments',
          sortable: true,
          label: 'Completed Assessments',
          thClass: 'ford-border ford-border-th',
          tdClass: 'ford-border ford-border-td'
        },
        {
          key: 'priority',
          sortable: true,
          label: 'Priority',
          thClass: 'ford-border ford-border-th',
          tdClass: 'ford-border ford-border-td'
        },
        {
          key: 'cdsid',
          sortable: true,
          label: 'CDSID',
          thClass: 'ford-border ford-border-th',
          tdClass: 'ford-border ford-border-td'
        },
      ],
      analytics: null,
    }
  },
  created() {
    this.loadParams();
    this.getAnalytics();
  },
  methods: {
    loadParams() {
      this.filter = this.$route.query.search;
      this.sortBy = this.$route.query.sortBy;
      this.sortDesc = Boolean(this.$route.query.sortDesc);
    },
    getAnalytics() {
      ApiService.query('api/v1/assessments/report_analytics')
      .then(({ data }) => {
        this.analytics = data;
      });
    },
    exportToCSV() {
      let items = this.$refs.analyticsTable.sortedItems;
      // console.table(items)

      items = items.map(
        obj => {
          // This is stupid
          let benchmarks = JSON.parse(obj.benchmarks);

          if (typeof benchmarks === "string") {
            benchmarks = JSON.parse(benchmarks);
          }

          let compensation = 0;
          let training = 0;
          let management = 0;
          let dealer_conditions = 0;
          let rewards = 0;

          compensation += benchmarks[0].headers[0].checkboxes.filter((data) => data.checked === true).length;
          compensation += benchmarks[0].headers[1].checkboxes.filter((data) => data.checked === true).length;
          compensation += benchmarks[0].headers[2].checkboxes.filter((data) => data.checked === true).length;

          training += benchmarks[1].headers[0].checkboxes.filter((data) => data.checked === true).length;
          training += benchmarks[1].headers[1].checkboxes.filter((data) => data.checked === true).length;
          training += benchmarks[1].headers[2].checkboxes.filter((data) => data.checked === true).length;
          training += benchmarks[1].headers[3].checkboxes.filter((data) => data.checked === true).length;

          management += benchmarks[2].headers[0].checkboxes.filter((data) => data.checked === true).length;
          management += benchmarks[2].headers[1].checkboxes.filter((data) => data.checked === true).length;
          management += benchmarks[2].headers[2].checkboxes.filter((data) => data.checked === true).length;

          dealer_conditions += benchmarks[3].headers[0].checkboxes.filter((data) => data.checked === true).length;
          dealer_conditions += benchmarks[3].headers[1].checkboxes.filter((data) => data.checked === true).length;
          dealer_conditions += benchmarks[3].headers[2].checkboxes.filter((data) => data.checked === true).length;
          dealer_conditions += benchmarks[3].headers[3].checkboxes.filter((data) => data.checked === true).length;

          rewards += benchmarks[4].headers[0].checkboxes.filter((data) => data.checked === true).length;
          rewards += benchmarks[4].headers[1].checkboxes.filter((data) => data.checked === true).length;
          rewards += benchmarks[4].headers[2].checkboxes.filter((data) => data.checked === true).length;

          let compensation_percent = (compensation === 0) ? 0 : (compensation/(3*3)).toFixed(3);
          let training_percent = (training === 0) ? 0 : (training/(3*4)).toFixed(3);
          let management_percent = (management === 0) ? 0 : (management/(3*3)).toFixed(3);
          let dealer_conditions_percent = (dealer_conditions === 0) ? 0 : (dealer_conditions/(3*4)).toFixed(3);
          let rewards_percent = (rewards === 0) ? 0 : (rewards/(3*3)).toFixed(3);

          let completion_date = new Date(obj.last_completion)

          return {
            "P&A Code": `="${obj.pa_code}"`,
            "Dealership Name": obj.dealership_name,
            "CDSID": obj.cdsid,
            "Priority": obj.priority,
            "Compensation": `${compensation_percent}`,
            "Training": `${training_percent}`,
            "Management": `${management_percent}`,
            "Dealer Conditions": `${dealer_conditions_percent}`,
            "Rewards & Recognition": `${rewards_percent}`,
            "Total Completed Assessments": obj.completed_assessments,
            "Last Assessment Date": completion_date.toISOString().substring(0, 10),
          }
        }
      )

      const csv = jsonToCSV(items);
      const timestamp = format(new Date(), 'yyyy/MM/dd hh:mm:ss a');
      localDownload(`Retention Assessment Analytics ${timestamp}.csv`, csv, 'text/csv')
    },
  },
  computed: {
    noResults() {
      return this.$refs.analyticsTable.sortedItems.length === 0
    },
    rows() {
      if (!this.analytics) return 0;
      return this.analytics.length;
    },
  },
  watch: {
    // TODO: This is causing a redundant navigation for some reason
    filter: {
      handler: debounce(function(newValue) {
        if (this.$route.query.search !== newValue) {
          this.$router.push({ query: Object.assign({}, this.$route.query, { search: newValue }) });
        }
      }, 500)
    },
    sortBy: {
      handler: function(newValue) {
        if (this.$route.query.sortBy !== newValue) {
          this.$router.push({ query: Object.assign({}, this.$route.query, { sortBy: newValue }) });
        }
      }
    },
    sortDesc: {
      handler: function(newValue) {
        if (this.$route.query.sortDesc !== newValue) {
          this.$router.push({ query: Object.assign({}, this.$route.query, { sortDesc: newValue }) });
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>

#analytics {
  padding: 25px 30px 40px;
}

/deep/ .table-striped tbody {
  tr:nth-of-type(odd) {
    background-color: #f6f8fa;
  }
  tr:nth-of-type(even) {
    background-color: white;
  }

  tr:last-of-type {
    border-bottom: 1px solid #d8d8d8;
  }
}

/deep/ .table {
  tr:first-of-type {
    th {
      padding-left: 0;
      padding-right: 0;
    }
  }
  th {
    border-top: unset;
  }
}

/deep/ .ford-border {

  &.ford-border-th {
    border: 1px solid #d8d8d8;
    color: #102b4e;
    font-size: 14px;
    font-weight: normal;
    text-transform: uppercase;
  }
  &.ford-border-td {
    border-left: 1px solid #d8d8d8;
    border-right: 1px solid #d8d8d8;
    border-top: unset;
    border-bottom: unset;
  }
}

.action-plan-btn {
  font-family: FordAntennaCond-Regular, sans-serif;
  background-color: $fordBlue;
  box-shadow: 0 20px 20px 0 rgba(0, 0, 0, 0.1),
  0 10px 10px 0 rgba(0, 0, 0, 0.1);
  font-size: 18px;
  height: 40px;
  display: flex;
  min-width: 280px;
  align-items: center;
  justify-content: center;
  text-align: center;
  @media (max-width: 576px) {
    min-width: 240px;
  }

  span {
    color: white;
  }

  &:hover {
    background-color: #2861a4;
  }

  svg {
    padding-left: 15px;
    font-size: 20px;

    path {
      fill: #fff;
    }
  }
  &.disabled {
    &:hover {
      text-decoration: none !important;
      background-color: $fordBlue;
      cursor: not-allowed;
    }
  }
}
</style>
